import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {BokkzIbDataService, GiftPeriodiek, IncomeTaxEntity} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type PeriodiekeGiftenFormGroup = FormGroup<{
    omschrijving: FormControl<string | null>;
    bedrag: FormControl<number | null>;
    cultureleAnbi: FormControl<boolean | null>;
    transactienummer: FormControl<number | null>;
    rsin: FormControl<string | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-periodieke-giften',
    templateUrl: './periodieke-giften.component.html',
    styleUrls: ['./periodieke-giften.component.scss'],
    imports: [SharedUiFormControlsModule, SharedUiButtonModule, SharedUiDetailsModule, CommonModule],
})
export class PeriodiekeGiftenComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private changeSubscriptions = new Subscription();

    public formGroup = new FormGroup({
        periodiekeGiften: new FormArray<PeriodiekeGiftenFormGroup>([]),
    });

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' },
    ];

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public ngOnInit(): void {
        this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
            this.updateForm(data.aangifte?.persoonsgebondenAftrek?.pgaAftrekbareGiften?.periodiekeGiften || []);
        });
        this.subscribeToChanges();
    }

    ngOnDestroy(): void {
        this.changeSubscriptions.unsubscribe();
    }

    private subscribeToChanges(): void {
        this.changeSubscriptions.unsubscribe();
        this.changeSubscriptions = new Subscription();

        const extractRelevant = () =>
            this.periodiekeGiften.getRawValue().map((w) => ({
                cultureleAnbi: w.cultureleAnbi,
                bedrag: w.bedrag,
            }));

        this.changeSubscriptions.add(
            this.periodiekeGiften.valueChanges
                .pipe(
                    debounceTime(500),
                    tap(() => this.saveForm()),
                    map(() => extractRelevant()),
                    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                    switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(),
        );
    }

    get periodiekeGiften(): FormArray {
        return this.formGroup.controls.periodiekeGiften;
    }

    addRij(): void {
        this.periodiekeGiften.push(this.createFormGroup(), { emitEvent: false });
    }

    verwijderRij(index: number): void {
        this.periodiekeGiften.removeAt(index, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.formGroup.markAsDirty({ emitEvent: false });
        this.saveForm();
    }

    createFormGroup(item?: GiftPeriodiek): FormGroup {
        return new FormGroup({
            omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
            bedrag: new FormControl<number | null>(item?.bedrag ?? null),
            cultureleAnbi: new FormControl<boolean>(item?.cultureleAnbi ?? false),
            transactienummer: new FormControl<number | null>(item?.transactienummer ?? null),
            rsin: new FormControl<string | null>(item?.rsin ?? null),
        });
    }

    private updateForm(data: GiftPeriodiek[]): void {
        const lijst = data || [];
        const formArray = this.periodiekeGiften;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(lijst[i], { emitEvent: false });
            } else {
                const formGroup = this.createFormGroup(lijst[i]);
                formArray.push(formGroup, { emitEvent: false });
            }
        }
    }

    saveForm(): void {
        const items = this.periodiekeGiften
            .getRawValue()
            .filter((gift) => gift.omschrijving !== null && gift.bedrag !== null)
            .map((gift) => ({
                omschrijving: gift.omschrijving!,
                bedrag: gift.bedrag!,
                rsin: gift.rsin,
                transactienummer: gift.transactienummer,
                cultureleAnbi: gift.cultureleAnbi ?? false,
            }));

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            return {
                ...currentEntity,
                aangifte: {
                    ...currentEntity.aangifte,
                    persoonsgebondenAftrek: {
                        ...currentEntity.aangifte.persoonsgebondenAftrek,
                        pgaAftrekbareGiften: {
                            ...currentEntity.aangifte.persoonsgebondenAftrek?.pgaAftrekbareGiften,
                            periodiekeGiften: items,
                        },
                    },
                },
            } as IncomeTaxEntity;
        });
    }
}
